import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// ── formatters ───────────────────────────────────────────────────────
export function formatRelative(fromIso, now) {
    if (!fromIso)
        return 'never';
    const then = Date.parse(fromIso);
    if (Number.isNaN(then))
        return fromIso;
    const deltaMs = now - then;
    if (deltaMs < 0)
        return 'just now';
    const sec = Math.floor(deltaMs / 1000);
    if (sec < 5)
        return 'just now';
    if (sec < 60)
        return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60)
        return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24)
        return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days === 1)
        return 'yesterday';
    if (days < 7)
        return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5)
        return `${weeks}w ago`;
    const months = Math.floor(days / 30);
    if (months < 12)
        return `${months}mo ago`;
    const years = Math.floor(days / 365);
    return `${years}y ago`;
}
/** GB-style byte formatter. Ollama reports sizes in bytes. */
export function formatBytes(bytes) {
    if (bytes === undefined || !Number.isFinite(bytes) || bytes < 0)
        return '—';
    const u = ['B', 'KB', 'MB', 'GB', 'TB'];
    let n = bytes;
    let i = 0;
    while (n >= 1024 && i < u.length - 1) {
        n /= 1024;
        i += 1;
    }
    return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}
// ── badges ───────────────────────────────────────────────────────────
const STATUS_CLASS = {
    active: 'bg-success',
    draining: 'bg-warning text-dark',
    archived: 'bg-secondary',
};
export function StatusBadge({ status }) {
    return (_jsx("span", { className: `badge ${STATUS_CLASS[status] ?? 'bg-secondary'}`, children: status }));
}
const OS_CLASS = {
    mac: 'bg-light text-dark border',
    macos: 'bg-light text-dark border',
    darwin: 'bg-light text-dark border',
    windows: 'bg-info text-dark',
    win: 'bg-info text-dark',
    linux: 'bg-dark text-light',
};
export function OsBadge({ os }) {
    const key = os.toLowerCase();
    return (_jsx("span", { className: `badge ${OS_CLASS[key] ?? 'bg-secondary'}`, children: os }));
}
export function CapabilityChips({ caps }) {
    if (!caps || caps.length === 0) {
        return _jsx("span", { className: "text-muted small", children: "\u2014" });
    }
    return (_jsx("span", { className: "d-inline-flex flex-wrap gap-1", children: caps.map((c) => (_jsx("span", { className: "badge bg-info text-dark", children: c }, c))) }));
}
// ── owner cell — lock icon for deploy_script, person for ui ─────────
export function OwnerBadge({ deployScriptManaged, }) {
    const icon = deployScriptManaged ? (
    // bi-lock-fill
    _jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 16 16", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" }) })) : (
    // bi-person-fill
    _jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 16 16", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" }) }));
    return (_jsxs("span", { className: "d-inline-flex align-items-center gap-1 small text-muted", title: deployScriptManaged
            ? 'Owned by deploy script — UI edits flip ownership'
            : 'Owned by UI — deploy script will skip this row', children: [icon, deployScriptManaged ? 'deploy_script' : 'ui'] }));
}
// ── tiny form helpers (subset of the ScheduledTasksPage Field) ───────
export function fieldClass(base, errs) {
    return errs && errs.length > 0 ? `${base} is-invalid` : base;
}
export function Field({ label, required, errors, hint, children }) {
    return (_jsxs("div", { className: "mb-3", children: [_jsxs("label", { className: "form-label mb-1", children: [label, required && _jsx("span", { className: "text-danger ms-1", children: "*" })] }), children, hint && !errors?.length && _jsx("div", { className: "form-text small", children: hint }), errors?.map((m, i) => (_jsx("div", { className: "invalid-feedback d-block small", children: m }, i)))] }));
}
export function ModalShell({ title, size = 'md', onClose, closeDisabled, footer, children, }) {
    const sizeClass = size === 'sm'
        ? 'modal-sm'
        : size === 'lg'
            ? 'modal-lg'
            : size === 'xl'
                ? 'modal-xl'
                : '';
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "modal show", style: { display: 'block' }, role: "dialog", "aria-modal": "true", children: _jsx("div", { className: `modal-dialog modal-dialog-centered ${sizeClass}`.trim(), children: _jsxs("div", { className: "modal-content", children: [_jsxs("div", { className: "modal-header", children: [_jsx("h5", { className: "modal-title", children: title }), _jsx("button", { type: "button", className: "btn-close", "aria-label": "Close", onClick: onClose, disabled: closeDisabled })] }), _jsx("div", { className: "modal-body", children: children }), _jsx("div", { className: "modal-footer", children: footer })] }) }) }), _jsx("div", { className: "modal-backdrop show" })] }));
}
// Ollama model categorisation + keep-alive helpers (P1-AI11 + P1-AI13)
// live in a sibling .ts module so they can be unit-tested without
// pulling .tsx (JSX) through `node --test`. Re-exported here so the
// existing import surface (`./shared`) keeps working.
export { categorizeOllamaModels, familyOfModel, parseParameterSizeB, readKeepAliveForModel, } from './categorize.js';
//# sourceMappingURL=shared.js.map